import { Component, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { logger } from '@/lib/logger';
import { captureException, isSentryEnabled } from '@/monitoring/sentry';
import i18n from '@/app/i18n/i18n';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      logger.error('[ErrorBoundary] Caught error:', error);
      logger.error('[ErrorBoundary] Error info:', errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Send to error tracking service (Sentry, etc.)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService(error: Error, errorInfo: React.ErrorInfo) {
    // Send to Sentry via our monitoring module
    if (isSentryEnabled()) {
      captureException(error, {
        componentStack: errorInfo.componentStack,
        boundaryType: 'global',
      });
    }

    // Log to custom analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: true,
      });
    }

    // Store in localStorage for debugging
    try {
      const errorLog = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      };

      const existingLogs = JSON.parse(localStorage.getItem('error_logs') || '[]');
      existingLogs.push(errorLog);

      // Keep only last 10 errors
      if (existingLogs.length > 10) {
        existingLogs.shift();
      }

      localStorage.setItem('error_logs', JSON.stringify(existingLogs));
    } catch (e) {
      logger.error('[ErrorBoundary] Failed to log error:', e);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleClearCacheAndReload = async () => {
    try {
      // Clear localStorage
      localStorage.clear();

      // Clear sessionStorage
      sessionStorage.clear();

      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      // Clear cache storage
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Hard reload (bypass cache)
      window.location.reload();
    } catch (error) {
      logger.error('[ErrorBoundary] Failed to clear cache:', error);
      // Fallback to normal reload
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const t = (key: string) => i18n.t(`toast.errorBoundary.${key}`, { ns: 'common' });

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-destructive" />
                <CardTitle>{t('title')}</CardTitle>
              </div>
              <CardDescription>{t('description')}</CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {import.meta.env.DEV && this.state.error && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{t('devOnly')}</p>
                  <div className="bg-muted p-3 rounded-md overflow-auto max-h-40">
                    <code className="text-xs text-muted-foreground">
                      {this.state.error.message}
                    </code>
                  </div>
                  {this.state.error.stack && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        {t('stackTrace')}
                      </summary>
                      <pre className="mt-2 bg-muted p-3 rounded-md overflow-auto max-h-60 text-muted-foreground">
                        {this.state.error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm font-medium">{t('whatYouCanDo')}</p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>{t('clearCacheRecommended')}</li>
                  <li>{t('tryRefresh')}</li>
                  <li>{t('contactSupport')}</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={this.handleClearCacheAndReload} className="w-full sm:w-auto">
                {t('clearCacheReload')}
              </Button>
              <Button onClick={this.handleReload} variant="outline" className="w-full sm:w-auto">
                {t('reloadPage')}
              </Button>
              <Button onClick={this.handleReset} variant="ghost" className="w-full sm:w-auto">
                {t('tryAgain')}
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook to manually trigger error boundary
 */
export function useErrorHandler() {
  return (error: Error) => {
    throw error;
  };
}

// Type augmentation for gtag and Sentry
declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params?: Record<string, unknown>) => void;
    Sentry?: {
      withScope: (
        callback: (scope: { setContext: (key: string, value: unknown) => void }) => void
      ) => void;
      captureException: (error: Error) => void;
    };
  }
}
